/**
 * The customer surface's language layer.
 *
 * ---------------------------------------------------------------------------
 * THE ONE RULE
 * ---------------------------------------------------------------------------
 * A guest must NEVER see `customer.menu.title` on their phone. `createTranslator`
 * from `@sonauto/i18n` enforces that: on a total miss it returns the fallback
 * bundle's text, or an empty string — never the key. Every miss is reported
 * through `onMissing` so the gap is visible in telemetry instead of on a
 * customer's screen.
 *
 * ---------------------------------------------------------------------------
 * NEGOTIATION
 * ---------------------------------------------------------------------------
 * `negotiateLocale` runs client-side with `navigator.languages` as the BROWSER
 * signal and the session's locale as the SESSION signal, over the branch's
 * enabled set from `GET /customer/session`. The server negotiates too (it sees
 * `Accept-Language`); doing it here as well means the switcher's initial
 * highlight matches what the guest is actually reading, and an offline reload
 * still picks a sensible language.
 *
 * Once the guest taps the switcher the choice is GUEST_CHOICE and is locked for
 * the life of the session — server-side by `localeLockedByGuest`, client-side by
 * `lockGuestChoice`. A Russian guest who picks Русский is never flipped back to
 * Vietnamese by a phone that also sends `Accept-Language: vi`.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createTranslator, formatPriceForCustomer, lockGuestChoice, negotiateLocale } from '@sonauto/i18n';
import type { Translator } from '@sonauto/i18n';
import type { CustomerApi } from '../lib/api';
import type { CustomerCurrency, CustomerLocaleOption } from '../lib/types';

export interface CustomerI18nState {
  readonly locale: string;
  readonly direction: 'ltr' | 'rtl';
  readonly locales: readonly CustomerLocaleOption[];
  readonly t: Translator['t'];
  /**
   * `t()` with a hard-coded Vietnamese last resort.
   *
   * `createTranslator` guarantees a key is never rendered raw, but on a total
   * miss it returns `''` — and an empty button is almost as bad as a raw key.
   * `tOr` is what every screen uses: translated string → vi-VN fallback bundle
   * → the source-language literal written at the call site. A guest always sees
   * words.
   */
  readonly tOr: (key: string, fallbackVi: string, params?: Readonly<Record<string, string | number>>) => string;
  readonly loadingStrings: boolean;
  /** Change language. Never touches the cart, never touches the currency. */
  readonly setLocale: (locale: string) => Promise<void>;
  /** Format money in the BRANCH's currency, grouped for the display locale. */
  readonly price: (amountMinor: number) => string;
  readonly currency: CustomerCurrency;
}

const CustomerI18nContext = createContext<CustomerI18nState | null>(null);

export function useCustomerI18n(): CustomerI18nState {
  const ctx = useContext(CustomerI18nContext);
  if (ctx === null) throw new Error('useCustomerI18n phải được dùng bên trong <CustomerI18nProvider>.');
  return ctx;
}

export interface CustomerI18nProviderProps {
  readonly api: CustomerApi;
  readonly initialLocale: string;
  readonly locales: readonly CustomerLocaleOption[];
  /**
   * The BRANCH's currency, taken from `GET /customer/session`.
   *
   * ── CURRENCY GUARANTEE #1 ────────────────────────────────────────────────
   * This value is a PROP, owned by the component that fetched the session, and
   * it is never derived from, or recomputed on, a locale change. `setLocale`
   * below deliberately ignores the `currency` field the API echoes back. A
   * Russian guest reading a Vietnamese menu sees `25 000 ₫`, not roubles: the
   * locale changes grouping and separators, nothing else.
   */
  readonly currency: CustomerCurrency;
  readonly onLocaleChanged?: (locale: string) => void;
  readonly children: ReactNode;
}

export function CustomerI18nProvider({
  api,
  initialLocale,
  locales,
  currency,
  onLocaleChanged,
  children,
}: CustomerI18nProviderProps): React.JSX.Element {
  const enabled = useMemo(() => locales.map((l) => l.locale), [locales]);

  const [locale, setLocaleState] = useState<string>(() => {
    const negotiated = negotiateLocale({
      // `navigator.languages` is the browser signal, in q-order already.
      acceptLanguage: typeof navigator === 'undefined' ? null : navigator.languages.join(','),
      sessionLocale: { locale: initialLocale, guestLocked: false },
      enabledLocales: enabled,
      branchDefault: locales.find((l) => l.isDefault)?.locale ?? null,
    });
    return negotiated.locale;
  });

  const [entries, setEntries] = useState<Record<string, string>>({});
  const [fallbackEntries, setFallbackEntries] = useState<Record<string, string>>({});
  const [loadingStrings, setLoadingStrings] = useState(true);
  const missingReported = useRef(new Set<string>());

  // Load the guest's bundle.
  useEffect(() => {
    let alive = true;
    const controller = new AbortController();
    setLoadingStrings(true);
    api
      .uiStrings(locale, { signal: controller.signal })
      .then((response) => {
        if (alive) setEntries(response.entries);
      })
      .catch(() => {
        // A failed bundle load must not blank the screen: the fallback bundle
        // (vi-VN) still renders, and `t()` never emits a raw key.
        if (alive) setEntries({});
      })
      .finally(() => {
        if (alive) setLoadingStrings(false);
      });
    return () => {
      alive = false;
      controller.abort();
    };
  }, [api, locale]);

  // Load the vi-VN fallback ONCE. It is what rescues a partially translated
  // branch: a missing Russian string renders in Vietnamese rather than blank.
  useEffect(() => {
    let alive = true;
    const controller = new AbortController();
    api
      .uiStrings('vi-VN', { signal: controller.signal })
      .then((response) => {
        if (alive) setFallbackEntries(response.entries);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
      controller.abort();
    };
  }, [api]);

  const translator = useMemo<Translator>(
    () =>
      createTranslator({
        bundle: { locale, namespace: 'customer', version: 0, generatedAt: Date.now(), entries },
        fallbackBundle: {
          locale: 'vi-VN',
          namespace: 'customer',
          version: 0,
          generatedAt: Date.now(),
          entries: fallbackEntries,
        },
        onMissing: (event) => {
          // De-duplicated so a list of forty items does not log forty times.
          const key = `${event.locale}:${event.key}`;
          if (missingReported.current.has(key)) return;
          missingReported.current.add(key);
          // eslint-disable-next-line no-console
          console.warn('[i18n] thiếu khoá dịch', event);
        },
      }),
    [entries, fallbackEntries, locale],
  );

  const setLocale = useCallback(
    async (next: string): Promise<void> => {
      const locked = lockGuestChoice(next, enabled);
      if (locked === null) return;
      // Optimistic: the strings reload from the effect above. The CART lives in
      // `CustomerApp`, several levels up, and is untouched by this state change.
      setLocaleState(locked.locale ?? next);
      onLocaleChanged?.(locked.locale ?? next);
      try {
        // ── CURRENCY GUARANTEE #2 ────────────────────────────────────────
        // The API echoes `currency` on this response. We deliberately DISCARD
        // it and keep the branch currency from the session. Assigning it here
        // is the one line that could ever make a language switch change money,
        // so it is the one line that does not exist.
        await api.setLocale(locked.locale ?? next);
      } catch {
        // The guest still reads the new language locally; the server will
        // re-negotiate on the next request.
      }
    },
    [api, enabled, onLocaleChanged],
  );

  // `<html lang>` and `dir` drive hyphenation, quotation marks and screen
  // readers. Set here rather than in index.html because the value is runtime.
  const direction = useMemo<'ltr' | 'rtl'>(() => {
    const info = locales.find((l) => l.locale === locale);
    return info?.direction.toUpperCase() === 'RTL' ? 'rtl' : 'ltr';
  }, [locale, locales]);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = direction;
    return () => {
      document.documentElement.lang = 'vi';
      document.documentElement.dir = 'ltr';
    };
  }, [direction, locale]);

  const value = useMemo<CustomerI18nState>(
    () => ({
      locale,
      direction,
      locales,
      t: translator.t,
      tOr: (key, fallbackVi, params) => {
        const value = translator.t(key, params);
        return value === '' ? fallbackVi : value;
      },
      loadingStrings,
      setLocale,
      // Branch currency + display locale. `formatPriceForCustomer` takes both
      // separately for exactly this reason.
      price: (amountMinor: number) => formatPriceForCustomer(amountMinor, currency.code, currency.exponent, locale),
      currency,
    }),
    [currency, direction, loadingStrings, locale, locales, setLocale, translator],
  );

  return <CustomerI18nContext.Provider value={value}>{children}</CustomerI18nContext.Provider>;
}
